import { useMemo, useState } from 'react';
import { CLASSES, childrenOf, getClass, type ClassNode } from '../../../shared/catalog/classes';
import { STYLE_CODE } from '../../../shared/catalog/variants';
import { MOD_DEFS, type ModDef } from '../../../shared/catalog/mods';
import { t, type Key } from '../i18n';
import { classDesc, className, familyName, groupName, modDesc, modName, needLabel } from '../i18n/catalog';
import { Icon } from './icons';
import { PageHead } from './MenuShell';
import { TankImage } from './TankPreview';
import { VariantPanel, useVariants } from './VariantPreview';

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Arbre complet consultable : 150 classes et 300 modifications, avec l'état réel d'implémentation. */
export function ClassTree({ bare, loggedIn }: { bare?: boolean; loggedIn: boolean }) {
  const [tab, setTab] = useState<'classes' | 'mods'>('classes');
  const [selected, setSelected] = useState('C01-F');
  const [family, setFamily] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const variants = useVariants(loggedIn);
  const roots = childrenOf('BASE');
  const playable = CLASSES.filter((c) => c.id !== 'BASE' && c.status === 'implemented').length;
  const q = norm(query.trim());
  // Recherche : une classe est gardée si elle ou un de ses descendants correspond.
  const visible = useMemo(() => {
    if (!q) return null;
    const keep = new Set<string>();
    const walk = (c: ClassNode): boolean => {
      const self = norm(`${className(c)} ${classDesc(c)} ${c.id}`).includes(q);
      const kid = childrenOf(c.id).map(walk).some(Boolean);
      if (self || kid) keep.add(c.id);
      return self || kid;
    };
    roots.forEach(walk);
    return keep;
  }, [q]);
  return (
    <div className={`page tree-page ${bare ? 'bare' : ''}`}>
      {!bare && <PageHead title={t('classTree')} sub={`${t('treeIntro')} ${t('treePlayable', { n: playable })}`} />}
      <div className="seg tree-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'classes'} onClick={() => setTab('classes')}>
          {t('treeClasses')}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'mods'} onClick={() => setTab('mods')}>
          {t('treeMods')}
        </button>
      </div>
      {tab === 'classes' ? (
        <div className="tree-layout">
          <aside className="tree-aside">
            <VariantPanel cls={getClass(selected)} variants={variants} />
            <p className="wip">{t('variantsIntro')}</p>
          </aside>
          <div className="tree-main">
            <div className="tree-tools">
              <input className="field tree-search" type="search" placeholder={t('treeSearch')} aria-label={t('treeSearch')} value={query} onChange={(e) => setQuery(e.target.value)} />
              <div className="family-chips" role="group" aria-label={t('treeFamily')}>
                <button type="button" className={`chip ${family === null ? 'chip-on' : ''}`} aria-pressed={family === null} onClick={() => setFamily(null)}>
                  {t('treeAll')}
                </button>
                {roots.map((r) => (
                  <button key={r.family} type="button" className={`chip ${family === r.family ? 'chip-on' : ''}`} aria-pressed={family === r.family} onClick={() => setFamily(r.family)}>
                    {familyName(r.family)}
                  </button>
                ))}
              </div>
            </div>
            <div className="tree">
              {roots
                .filter((root) => (family === null || root.family === family) && (!visible || visible.has(root.id)))
                .map((root) => (
                  <section key={root.id} className="tree-family plate">
                    <h3 className="tree-family-title">
                      <span className="display">{familyName(root.family)}</span>
                      <small className="num">{root.id.slice(0, 3)}</small>
                    </h3>
                    <Node node={root} onSelect={setSelected} selected={selected} visible={visible} choices={variants.data?.choices} />
                  </section>
                ))}
              {visible && visible.size === 0 && <p className="wip">{t('treeNoResult')}</p>}
            </div>
          </div>
        </div>
      ) : (
        <ModList />
      )}
    </div>
  );
}

const SLOTS = ['weapon', 'chassis', 'passive'] as const;

function ModList() {
  const [slot, setSlot] = useState<(typeof SLOTS)[number] | null>(null);
  const [query, setQuery] = useState('');
  const q = norm(query.trim());
  const groups = new Map<string, ModDef[]>();
  for (const d of Object.values(MOD_DEFS)) {
    if (slot && d.slot !== slot) continue;
    if (q && !norm(`${modName(d)} ${modDesc(d)} ${groupName(d.group)}`).includes(q)) continue;
    const key = `${d.slot}|${d.group}`;
    const list = groups.get(key) ?? [];
    list.push(d);
    groups.set(key, list);
  }
  return (
    <div className="mods-page">
      <div className="tree-tools">
        <input className="field tree-search" type="search" placeholder={t('modsSearch')} aria-label={t('modsSearch')} value={query} onChange={(e) => setQuery(e.target.value)} />
        <div className="seg" role="group" aria-label={t('modsSlot')}>
          <button type="button" aria-pressed={slot === null} onClick={() => setSlot(null)}>
            {t('treeAll')}
          </button>
          {SLOTS.map((s) => (
            <button key={s} type="button" aria-pressed={slot === s} onClick={() => setSlot(s)}>
              {t(`slot_${s}` as Key)}
            </button>
          ))}
        </div>
      </div>
      <div className="mod-grid">
        {[...groups.entries()].map(([key, list]) => (
          <section key={key} className="tree-family plate">
            <h3 className="tree-family-title">
              <span className="display">{groupName(list[0].group)}</span>
              <small>{t(`slot_${list[0].slot}` as Key)}</small>
            </h3>
            {list.map((d) => (
              <div key={d.id} className="mod-row">
                <span className="tree-level num">{d.id.slice(4)}</span>
                <span className="mod-row-body">
                  <strong>{modName(d)}</strong>
                  <span className="tree-desc">{modDesc(d)}</span>
                  <span className="mod-compat">{t('modCompat', { list: d.needs?.map((n) => needLabel(n)).join(', ') ?? t('modAny') })}</span>
                </span>
                {d.teamOnly && <span className="badge badge-brass">{t('modTeamOnly')}</span>}
              </div>
            ))}
          </section>
        ))}
        {groups.size === 0 && <p className="wip">{t('treeNoResult')}</p>}
      </div>
    </div>
  );
}

function Node({
  node,
  onSelect,
  selected,
  visible,
  choices,
}: {
  node: ClassNode;
  onSelect: (id: string) => void;
  selected: string;
  visible: Set<string> | null;
  choices?: Record<string, string>;
}) {
  const kids = childrenOf(node.id).filter((k) => !visible || visible.has(k.id));
  const style = (choices?.[node.id] ?? 'atelier') as keyof typeof STYLE_CODE;
  return (
    <div className="tree-node">
      <div className={`tree-item ${node.status} ${selected === node.id ? 'selected' : ''}`}>
        <span className="tree-thumb" aria-hidden>
          <TankImage cls={node} look={{ code: STYLE_CODE[style] }} size={40} />
        </span>
        <span className="tree-level num">{node.level}</span>
        <span className="tree-text">
          <button type="button" className="tree-name" onClick={() => onSelect(node.id)} aria-describedby={`desc-${node.id}`}>
            {className(node)}
          </button>
          <span className="tree-desc" id={`desc-${node.id}`}>
            {classDesc(node)}
          </span>
        </span>
        {node.status !== 'implemented' && <span className="badge">{t('planned')}</span>}
        {selected === node.id && (
          <span className="tree-sel" aria-hidden>
            <Icon name="target" size={14} />
          </span>
        )}
      </div>
      {kids.length > 0 && (
        <div className="tree-children">
          {kids.map((k) => (
            <Node key={k.id} node={k} onSelect={onSelect} selected={selected} visible={visible} choices={choices} />
          ))}
        </div>
      )}
    </div>
  );
}
